import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiChevronDown } from "react-icons/hi";

import FadeIn from "../components/Animation/FadeIn";
import IFBanner from "../components/IF_Template/IF_banner";
import IFPricing from "../components/IF_Template/IF_Pricing";
import IFSubMenu from "../components/IF_SubMenu";
import IFSwiper from "../components/IF_Swiper";
import IFHeading from "../components/IF_Heading";
import { getPostural, getPosturalCache } from "../services";

function SafeJsonParse(str, fallback = []) {
    try {
        return JSON.parse(str);
    } catch {
        return fallback;
    }
}

function SpineProblemCard({ item, index }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: index * 0.1, ease: "linear" }}
        >
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`cursor-pointer rounded-2xl p-5 border transition-colors duration-200 ${isOpen
                    ? "bg-pink/15 border-pink"
                    : "bg-[#12141e] border-white/10 hover:border-pink"
                    }`}
            >
                <div className="flex justify-between items-center">
                    <h4
                        className={`text-sm sm:text-base font-extrabold tracking-wide ${isOpen ? "text-pink" : "text-white"
                            }`}
                    >
                        {item.title.toUpperCase()}
                    </h4>
                    <HiChevronDown
                        className={`text-xl text-pink transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"
                            }`}
                    />
                </div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: "linear" }}
                            className="overflow-hidden"
                        >
                            <div className="pt-4 border-t border-white/10 mt-3">
                                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed whitespace-pre-line">
                                    {item.description}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}

export default function Postural() {
    const cached = getPosturalCache();
    const [data, setDatas] = useState(() => cached || { datas: [], album: [] });
    const [load, setLoad] = useState(() => !cached || !cached.datas || cached.datas.length === 0);

    useEffect(() => {
        getPostural(true).then((res) => {
            if (res && res.datas && res.datas.length > 0) {
                setDatas(res);
            }
            setLoad(false);
        });
    }, []);

    if (load || !data.datas || data.datas.length === 0) {
        return (
            <div className="min-h-[70vh] w-full flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-pink animate-spin" />
            </div>
        );
    }

    const spineProblems = SafeJsonParse(data.datas[4]?.value, []);
    const pricingDatas = SafeJsonParse(data.datas[8]?.value, []);

    return (
        <div className="flex flex-col w-full overflow-x-hidden">
            {/* Banner Section */}
            <IFBanner>
                <FadeIn direction="left" duration={0.9}>
                    <div className="flex flex-col-reverse lg:flex-row gap-6 lg:gap-10 items-center">
                        <div className="flex-1">
                            <IFSubMenu
                                as="h1"
                                text1={data.datas[0]?.value}
                                text2={data.datas[1]?.value}
                            />
                            <p className="text-xs sm:text-sm md:text-base text-gray-300 leading-relaxed mt-3">
                                {data.datas[2]?.value}
                            </p>
                        </div>

                        {/* Video Player (Zero Blur) */}
                        {data.datas[3]?.value && (
                            <div className="rounded-2xl overflow-hidden border border-pink/35 bg-black max-w-full md:max-w-[360px] w-full">
                                <video
                                    controls
                                    autoPlay
                                    muted
                                    playsInline
                                    className="w-full h-[220px] object-cover"
                                    src={process.env.REACT_APP_IMAGE_URL + data.datas[3].value}
                                />
                            </div>
                        )}
                    </div>
                </FadeIn>
            </IFBanner>

            {/* Spine Problems Section */}
            <section className="py-12 md:py-16 px-4 md:px-8">
                <IFHeading>MASALAH PADA TULANG BELAKANG</IFHeading>

                <div className="max-w-[1200px] mx-auto mt-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {spineProblems.map((item, index) => (
                            <SpineProblemCard key={index} item={item} index={index} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Consultation & Assessment Section */}
            <section className="py-12 md:py-16 px-4 md:px-8">
                <IFHeading>KONSULTASI & ASSESSMENT</IFHeading>

                <div className="max-w-[1100px] mx-auto mt-8">
                    <div className="p-6 md:p-10 rounded-3xl bg-[#12141e] border border-white/10 flex flex-col items-center text-center gap-8">
                        <FadeIn duration={0.9}>
                            <p className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed whitespace-pre-line max-w-[850px] m-auto">
                                {data.datas[5]?.value}
                            </p>
                        </FadeIn>

                        {data.datas[6]?.value && (
                            <FadeIn delay={0.2} duration={0.9}>
                                <div className="rounded-2xl overflow-hidden bg-white border border-pink/30 max-w-[700px] mx-auto">
                                    <img
                                        alt="Konsultasi & Assessment"
                                        src={process.env.REACT_APP_IMAGE_URL + data.datas[6].value}
                                        className="w-full h-auto block"
                                    />
                                </div>
                            </FadeIn>
                        )}
                    </div>
                </div>
            </section>

            {/* Postural Program Studio & Home */}
            <section className="py-12 md:py-16">
                <IFHeading>POSTURAL PROGRAM STUDIO & HOME</IFHeading>
                <IFPricing datas={pricingDatas}>
                    <FadeIn duration={0.9}>
                        <p className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed whitespace-pre-line">
                            {data.datas[7]?.value}
                        </p>
                    </FadeIn>
                </IFPricing>
            </section>

            {/* Album Swiper */}
            <IFSwiper data={data.album} />
        </div>
    );
}
